import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatDialog } from '@angular/material/dialog'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { ActivatedRoute, Router } from '@angular/router'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { debounceTime, map, Subject, switchMap } from 'rxjs'
import { CallsService, ClientCall } from '../calls.service'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import {
  UndoSoftDeletionDialogComponent,
  UndoSoftDeletionDialogData,
  UndoSoftDeletionDialogResult,
} from '../call/undo-soft-deletion-dialog/undo-soft-deletion-dialog.component'
import { emitPaginationQueryParameterChanges, getPaginationOptionsFromQueryParamMap } from '../list-query-options'
import { DateRange, DateRangePickerComponent, readDateRangeFromQueryParams } from '../common/date-range-picker/date-range-picker.component'
import { ChipCellValue } from '../paginated-table/paginated-table-cell/paginated-table-cell.component'
import { AdminPaginatedTableAction, ColumnDefinition, PaginatedTableComponent, PaginatedTableEvent } from '../paginated-table/paginated-table.component'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import {
  ExtendRetentionDialogComponent,
  ExtendRetentionDialogData,
  ExtendRetentionDialogResult,
} from './extend-retention-dialog/extend-retention-dialog.component'
import { ManageCallUsersDialogComponent, ManageCallUsersDialogData } from './manage-call-users-dialog/manage-call-users-dialog.component'
import {
  RetentionExtensionHistoryDialogComponent,
  RetentionExtensionHistoryDialogData,
} from './retention-extension-history-dialog/retention-extension-history-dialog.component'

const DEFAULT_PAGE_SIZE = 25

@Component({
  selector: 'app-admin-calls-page',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatSlideToggle,
    DateRangePickerComponent,
    PaginatedTableComponent,
    SpinnerComponent,
    ToolbarComponent,
    TranslatePipe,
  ],
  templateUrl: './admin-calls-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-calls-page.component.scss',
})
export class AdminCallsPageComponent implements OnInit {
  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  route = inject(ActivatedRoute)
  router = inject(Router)
  translateService = inject(TranslateService)

  defaultPageSize = DEFAULT_PAGE_SIZE

  /** Meetings a new assistant has been sent to in this sitting, so the action cannot be repeated. */
  private callIdsSentARecoveryAssistant = new Set<number>()
  nameQueryUpdate$ = new Subject<string>()

  extendRetentionPeriodMutation = this.callsService.extendRetentionPeriodMutation()
  restoreCallMutation = this.callsService.restoreCallMutation()
  sendRecoveryBotMutation = this.callsService.sendRecoveryBotMutation()

  nameQuery$ = this.route.queryParamMap.pipe(map((queryParamMap) => queryParamMap.get('name') ?? ''))
  includeAllStatuses$ = this.route.queryParamMap.pipe(map((queryParamMap) => queryParamMap.get('includeAllStatuses') === 'true'))
  page$ = this.route.queryParamMap.pipe(
    map((queryParamMap) => getPaginationOptionsFromQueryParamMap(queryParamMap, null, { pageSize: DEFAULT_PAGE_SIZE }).page)
  )
  dateRange$ = this.route.queryParamMap.pipe(map((queryParamMap) => readDateRangeFromQueryParams(queryParamMap)))

  calls$ = this.route.queryParamMap.pipe(
    debounceTime(300),
    switchMap((queryParamMap) => {
      const { page, pageSize, sortField, sortDirection } = getPaginationOptionsFromQueryParamMap(queryParamMap, null, {
        pageSize: DEFAULT_PAGE_SIZE,
        sortField: 'date',
        sortDirection: 'desc',
      })
      return this.callsService.getClientCalls(
        queryParamMap.get('name'),
        queryParamMap.get('includeAllStatuses') === 'true',
        readDateRangeFromQueryParams(queryParamMap),
        sortField,
        sortDirection ?? 'desc',
        page,
        pageSize
      )
    })
  )

  ngOnInit() {
    this.nameQueryUpdate$.pipe(debounceTime(500)).subscribe((value) => this.updateNameQuery(value))
  }

  // Narrowing the list can leave the current page beyond the last one, so both filters send the
  // reader back to the first page. The table follows through its `page` input.
  private async updateNameQuery(value: string) {
    await this.router.navigate([], {
      queryParams: { name: value === '' ? null : value, page: 0 },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async updateDateRange(range: DateRange) {
    await this.router.navigate([], {
      queryParams: {
        startDate: range.startDate === null ? null : range.startDate.toISOString(),
        endDate: range.endDate === null ? null : range.endDate.toISOString(),
        page: 0,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  async toggleIncludeAllStatuses(includeAllStatuses: boolean) {
    await this.router.navigate([], {
      queryParams: { includeAllStatuses: includeAllStatuses ? true : null, page: 0 },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    })
  }

  handleListOptionsChange(event: PaginatedTableEvent) {
    emitPaginationQueryParameterChanges(this.router, this.route, event)
  }

  columnDefinitions: ColumnDefinition<ClientCall>[] = [
    {
      columnDef: 'id',
      type: 'NUMBER',
      header: 'COMMON.ID',
      cell: (call: ClientCall) => call.id,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'name',
      type: 'TEXT',
      header: 'COMMON.NAME',
      cell: (call: ClientCall) => call.name,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'organizationName',
      type: 'TEXT',
      header: 'COMMON.ORGANIZATION',
      cell: (call: ClientCall) => call.organizationName,
      sortable: false,
      sticky: false,
    },
    {
      columnDef: 'date',
      type: 'DATE',
      header: 'COMMON.DATE_AND_TIME',
      cell: (call: ClientCall) => call.date,
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'duration',
      type: 'DURATION',
      header: 'COMMON.DURATION',
      cell: (call: ClientCall) => call.duration,
      sortable: false,
      sticky: false,
    },
    {
      columnDef: 'users',
      type: 'USER_AVATAR_LIST',
      header: 'COMMON.USERS',
      cell: (call: ClientCall) => call.users.map((user) => ({ name: `${user.firstName} ${user.lastName}`, id: user.userId })),
      sortable: false,
      sticky: false,
    },
    {
      columnDef: 'status',
      type: 'CHIP',
      header: 'COMMON.STATUS',
      cell: (call: ClientCall) => this.describeStatus(call),
      sortable: true,
      sticky: false,
    },
    {
      columnDef: 'deletionDate',
      type: 'DATE',
      header: 'ADMIN.CALLS.DELETION_DATE',
      cell: (call: ClientCall) => call.deletionDate,
      sortable: false,
      sticky: false,
    },
    {
      columnDef: 'markedToBeDeletedAt',
      type: 'DATE',
      header: 'ADMIN.CALLS.SOFT_DELETED_AT',
      cell: (call: ClientCall) => call.markedToBeDeletedAt,
      sortable: true,
      sticky: false,
    },
  ]

  actions: AdminPaginatedTableAction<ClientCall>[] = [
    {
      translationKey: 'CALLS.EXTEND_RETENTION.BUTTON',
      icon: 'more_time',
      handle: (call: ClientCall) => this.extendRetentionPeriod(call),
      isVisible: (call: ClientCall) => call.canExtendRetentionPeriod,
    },
    {
      translationKey: 'CALLS.EXTEND_RETENTION.HISTORY_BUTTON',
      icon: 'history',
      handle: (call: ClientCall) => this.showRetentionExtensionHistory(call),
      isVisible: (call: ClientCall) => call.retentionExtensionCount > 0,
    },
    {
      translationKey: 'CALLS.ADMIN.SOFT_DELETION.UNMARK_TITLE',
      icon: 'restore_from_trash',
      handle: (call: ClientCall) => this.undoSoftDeletion(call),
      isVisible: (call: ClientCall) => call.markedToBeDeletedAt !== null,
    },
    {
      translationKey: 'CALLS.ADMIN.MANAGE_CALL_USERS.BUTTON',
      icon: 'manage_accounts',
      handle: (call: ClientCall) => this.manageCallUsers(call),
    },
    {
      translationKey: 'ADMIN.CALLS.SEND_NEW_ASSISTANT',
      disabledTranslationKey: 'ADMIN.CALLS.SEND_NEW_ASSISTANT_UNAVAILABLE',
      icon: 'smart_toy',
      handle: (call: ClientCall) => this.sendRecoveryBot(call),
      isVisible: (call: ClientCall) => call.isOngoing || call.isCompleted,
      isDisabled: (call: ClientCall) => !call.canSendRecoveryAssistant || this.callIdsSentARecoveryAssistant.has(call.id),
    },
  ]

  /**
   * The lifecycle of a meeting as one chip: what the assistant is doing right now takes precedence
   * over the meeting's own status, which for an ongoing meeting still reads as merely created.
   */
  private describeStatus(call: ClientCall): ChipCellValue {
    if (call.markedToBeDeletedAt !== null) {
      return { label: this.translateService.instant('ADMIN.CALLS.STATUS_SOFT_DELETED'), variant: 'danger' }
    }
    if (call.isOngoing) {
      const sessionStatus = call.latestCallSessionStatus
      return {
        label:
          sessionStatus === null
            ? this.translateService.instant('ADMIN.CALLS.STATUS_ONGOING')
            : this.translateService.instant(`CALLS.STATUSES.${sessionStatus}`),
        variant: 'warning',
      }
    }
    if (call.processingStatus !== null) {
      return { label: this.translateService.instant(`CALLS.PROCESSING_STATUSES.${call.processingStatus}`), variant: 'neutral' }
    }
    if (call.status === 'COMPLETED') {
      return { label: this.translateService.instant('CALLS.STATUSES.COMPLETED'), variant: 'success' }
    }
    if (call.status === 'ERRORED' || call.status === 'TIMEOUT') {
      return { label: this.translateService.instant(`CALLS.STATUSES.${call.status}`), variant: 'danger' }
    }
    return { label: this.translateService.instant(`ADMIN.CALLS.STATUSES.${call.status}`), variant: 'neutral' }
  }

  private extendRetentionPeriod(call: ClientCall) {
    if (call.deletionDate === null) {
      return
    }
    const data: ExtendRetentionDialogData = { callName: call.name, currentDeletionDate: call.deletionDate, maximumDeletionDate: call.maximumDeletionDate }
    const dialogRef = this.dialog.open(ExtendRetentionDialogComponent, { width: '500px', data })
    // A dialog dismissed with the escape key or the backdrop closes with no value at all, so the
    // result is normalised before it is read.
    dialogRef
      .afterClosed()
      .pipe(map((result: ExtendRetentionDialogResult | null | undefined) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.extendRetentionPeriodMutation.mutateAsync({ callId: call.id, deletionDate: result.deletionDate })
        }
      })
  }

  private showRetentionExtensionHistory(call: ClientCall) {
    const data: RetentionExtensionHistoryDialogData = { callId: call.id, callName: call.name }
    this.dialog.open(RetentionExtensionHistoryDialogComponent, { width: '640px', data })
  }

  private undoSoftDeletion(call: ClientCall) {
    const data: UndoSoftDeletionDialogData = { callId: call.id, currentRetentionPeriodStartDate: call.retentionPeriodStartDate }
    const dialogRef = this.dialog.open(UndoSoftDeletionDialogComponent, { width: '500px', data })
    dialogRef
      .afterClosed()
      .pipe(map((result: UndoSoftDeletionDialogResult | null | undefined) => result ?? null))
      .subscribe(async (result) => {
        if (result !== null) {
          await this.restoreCallMutation.mutateAsync({ callId: call.id, retentionPeriodStartDate: result.retentionPeriodStartDate })
        }
      })
  }

  private manageCallUsers(call: ClientCall) {
    const data: ManageCallUsersDialogData = { callId: call.id, callName: call.name, users: call.users }
    this.dialog.open(ManageCallUsersDialogComponent, { width: '560px', data, autoFocus: 'first-heading' })
  }

  private sendRecoveryBot(call: ClientCall) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('ADMIN.CALLS.SEND_NEW_ASSISTANT'),
        description: this.translateService.instant('CALLS.ADMIN.SEND_RECOVERY_BOT.DESCRIPTION'),
      },
    })
    dialogRef
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true))
      .subscribe(async (confirmed) => {
        if (confirmed) {
          // Marked before the request goes out, so a second click cannot land while the first is in
          // flight. A rejected request releases the lock again: the endpoint refuses a duplicate on
          // its own, because a meeting that was sent one is left waiting for a retry.
          this.callIdsSentARecoveryAssistant.add(call.id)
          try {
            await this.sendRecoveryBotMutation.mutateAsync(call.id)
          } catch {
            this.callIdsSentARecoveryAssistant.delete(call.id)
          }
        }
      })
  }
}
